import React from 'react'
import { Snowflake, Caravan } from 'lucide-react'

/**
 * 「这一单要哪种发电机」的那对互斥图标 —— 可编辑的那一份。
 *
 * 和 GensetMark 的分工
 * -------------------
 * GensetMark 是**只读**的标记，跟在板子/司机卡的底盘号后面，只画 genset 和
 * genset_chassis 两种，其余什么都不画。
 * 这个是**可改**的控件：建单窗口、任务详情、订单详情三处都要能点，所以合成一处。
 * 上一版 genset 只做在建单窗口里，建完就改不了了 —— 客户后来改口要挂机，调度员
 * 只能把整张单删了重建。
 *
 * 为什么是两个图标而不是一个开关
 * -----------------------------
 *   Genset          一台独立的发电机，去堆场单独提、用完单独还，有租金、要加油。
 *                   这一趟因此**多出两脚活**。❄ 雪花 —— 它是一台独立的设备。
 *   Genset Chassis  发电机焊在底盘上，是底盘的一种型号，没有额外的提还。
 *                   🚚 拖车厢 —— 说的是这台底盘。
 *
 * 两者互斥，点亮的那个再点一下就灭 —— 两个都灭＝这趟明确不要发电机（'none'）。
 *
 * 四态里的最后一种在这里看不出来：`undefined` 是「没人说过」（存量订单），
 * 界面上和 'none' 长得一样。分开存是为了以后能答「这单问过没有」，所以
 * **onChange 只在用户点过之后才发** —— 没点过的单子不会被悄悄改写成 'none'。
 */

export type GensetType = 'none' | 'genset' | 'genset_chassis'

const CHOICES = [
  {
    kind: 'genset' as const,
    Icon: Snowflake,
    idSuffix: 'toggle',
    name: 'Genset',
    label: 'Genset required',
    title: 'Genset — a standalone unit: separate pick-up and return at the yard, needs fuel on the road',
  },
  {
    kind: 'genset_chassis' as const,
    Icon: Caravan,
    idSuffix: 'chassis-toggle',
    name: 'Genset Chassis',
    label: 'Genset chassis required',
    title: 'Genset Chassis — generator mounted on the chassis; no extra pick-up or return',
  },
]

interface GensetPickerProps {
  value: GensetType | undefined
  onChange: (next: GensetType) => void
  /**
   * 两个按钮的 id 前缀。默认 `genset` —— 建单窗口那两个 id
   * (`genset-toggle` / `genset-chassis-toggle`) 是测试和既有代码在用的名字。
   * 同一屏可能同时存在第二份控件（任务详情开在订单详情上面），所以要能换前缀。
   */
  idPrefix?: string
  /**
   * 左边那句 "Genset:"。两个名字已经各自写在图标后面了，所以默认不再加这句前缀
   * ——「Genset: ❄ Genset 🚚 Genset Chassis」读起来是重复的。
   * 外面没有任何标题可依靠时才打开。
   */
  showLabel?: boolean
  className?: string
  disabled?: boolean
}

export function GensetPicker({
  value,
  onChange,
  idPrefix = 'genset',
  showLabel = false,
  className = '',
  disabled = false,
}: GensetPickerProps) {
  return (
    // role=radiogroup + aria-checked：对读屏和测试来说这是一组单选。
    <div className={`flex items-center gap-2 h-[38px] ${className}`} role="radiogroup" aria-label="Genset">
      {showLabel && <span className="text-sm font-medium text-gray-700">Genset:</span>}
      {CHOICES.map(({ kind, Icon, idSuffix, name, label, title }) => {
        const on = value === kind
        return (
          <button
            key={kind}
            type="button"
            role="radio"
            id={`${idPrefix}-${idSuffix}`}
            aria-checked={on}
            aria-label={label}
            disabled={disabled}
            onClick={() => onChange(on ? 'none' : kind)}
            title={title}
            // 名字写在图标旁边 —— 光看一朵雪花和一节拖车厢，分不出哪个是「独立
            // 发电机」哪个是「焊在底盘上的」，而认错就是司机多跑一趟或漏一脚。
            // 整块（图标＋名字）都是点击区，不只是图标。
            className={`flex items-center gap-1.5 h-[38px] px-2 rounded-lg transition-colors whitespace-nowrap ${
              disabled ? 'cursor-not-allowed opacity-50' : ''
            } ${on ? 'text-cyan-600 bg-cyan-50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
          >
            <Icon className={`shrink-0 transition-all ${on ? 'w-5 h-5' : 'w-[18px] h-[18px]'}`} />
            <span className={`text-sm ${on ? 'font-medium' : ''}`}>{name}</span>
          </button>
        )
      })}
    </div>
  )
}

export default GensetPicker
